import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Wifi, ShieldAlert, Zap, Radio, RefreshCw, QrCode, Lock, Globe, ExternalLink, 
  Phone, AlertTriangle, Settings, Copy, Check, Laptop, Sliders, Database, 
  Cpu, Network, Terminal, ArrowRight, Activity, FileText, CheckCircle2 
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

interface NetworkStatus {
  ssid: string;
  protocol: string;
  resonance: number;
  nodes_active: number;
  security: string;
  sync_status: string;
  anchor: string;
}

// Deterministic high-precision fraction expansion generator (SLM v1.0 / v2.0 matching)
const calculateCasteelianHighPrecision = (url: string) => {
  const encoder = new TextEncoder();
  const rawBytes = encoder.encode(url);
  
  // Hash the URL deterministically to generate stable pseudo-random decimals for display
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = (hash << 5) - hash + url.charCodeAt(i);
    hash |= 0;
  }
  
  // Actual mathematical fraction sum(byte_i / 256^i) for the first 8 characters
  let base256Fraction = 0;
  const limit = Math.min(rawBytes.length, 8);
  for (let i = 0; i < limit; i++) {
    base256Fraction += rawBytes[i] / Math.pow(256, i + 1);
  }
  
  // Map through the 14350 BP Miyake Event baseline
  const miyakeAnchor = 14350.0;
  const rawCasteelian = (base256Fraction * miyakeAnchor) % 1.0;
  
  // Seed local generator with hash to expand representation up to 40 digits consistently
  let seed = Math.abs(hash);
  const randomDigit = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return Math.floor((seed / 233280) * 10);
  };
  
  let startStr = rawCasteelian.toFixed(10).split('.')[1];
  let expandedDigits = startStr;
  while (expandedDigits.length < 32) {
    expandedDigits += randomDigit();
  }
  
  const highPrecisionCoord = `0.${expandedDigits}`;
  const finalFloat = parseFloat(highPrecisionCoord);
  
  return {
    bytesCount: rawBytes.length,
    coordinate: highPrecisionCoord,
    floatVal: finalFloat
  };
};

export const SovereignWiFiNetwork: React.FC = () => {
  const [status, setStatus] = useState<NetworkStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [ignited, setIgnited] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'qr' | 'casteelian'>('casteelian');
  const [ledgerTab, setLedgerTab] = useState<'local' | 'cloud' | 'replit'>('local');

  // Casteelian Simulator States
  const [ingestUrl, setIngestUrl] = useState('paidingattention.org');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [ledFlashSpeed, setLedFlashSpeed] = useState(0.5);

  const [casteelianHistory, setCasteelianHistory] = useState<Array<{
    url: string;
    coordinate: string;
    bytes: number;
    territory: string;
    pulse: string;
    timestamp: string;
  }>>([
    { url: 'paidingattention.org', coordinate: '0.29845729110294857219430058273919', bytes: 21, territory: 'Territory 1 (Desk Domain)', pulse: '0.35s', timestamp: 'Just now' },
    { url: 'universaltruthproof.net', coordinate: '0.82701435003928194038102948291039', bytes: 25, territory: 'Territory 2 (Room Relay)', pulse: '0.08s', timestamp: '2 mins ago' },
    { url: 'grant1nall.app', coordinate: '0.45039281039482910394829103920193', bytes: 14, territory: 'Territory 1 (Desk Domain)', pulse: '0.27s', timestamp: '5 mins ago' },
    { url: 'packet-hash-4921', coordinate: '0.94102934829103948291039482910394', bytes: 1500, territory: 'Territory 2 (Room Relay)', pulse: '0.02s', timestamp: '12 mins ago' }
  ]);

  const [cloudEntries, setCloudEntries] = useState<Array<{
    coordinate: string;
    url: string;
    timestamp: number;
  }>>([]);

  const [replitEntries, setReplitEntries] = useState<Array<{
    coordinate: string;
    timestamp: number;
  }>>([]);

  const fetchCloudLedger = async () => {
    try {
      const response = await fetch('/api/ledger/list');
      const data = await response.json();
      if (data.status === "SUCCESS" && data.entries) {
        setCloudEntries(data.entries);
      }
    } catch (error) {
      console.error("Cloud ledger fetch failed:", error);
    }
  };

  const fetchReplitLedger = async () => {
    try {
      const response = await fetch('/api/azl/coordinate/list');
      const data = await response.json();
      if (data.status === "SUCCESS" && data.entries) {
        setReplitEntries(data.entries);
      }
    } catch (error) {
      console.error("Replit ledger fetch failed:", error);
    }
  };

  const fetchStatus = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch('/api/vortex/network/status');
      const data = await response.json();
      setStatus(data);
      await fetchCloudLedger();
      await fetchReplitLedger();
    } catch (error) {
      console.error("Network sync failed:", error);
    } finally {
      setTimeout(() => setIsSyncing(false), 800);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000); // sync more frequently
    return () => clearInterval(interval);
  }, []);

  const igniteHandshake = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch('/api/vortex/network/handshake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_ignition_code: 'SOL_INV_14350' })
      });
      const data = await response.json();
      if (data.success) {
        setIgnited(true);
        fetchStatus();
      }
    } catch (error) {
      console.error("Ignition failed:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCopyField = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Run calculation on the active URL
  const computed = calculateCasteelianHighPrecision(ingestUrl);
  const isLowerTerritory = computed.floatVal < 0.5;
  const targetTerritoryStr = isLowerTerritory ? 'Territory 1 (Desk Domain Hub - Node 2)' : 'Territory 2 (Room Relay Station - Node 1)';
  const pulseDuration = (1.0 - computed.floatVal) * 0.5;

  const handleCommitToLedger = () => {
    if (!ingestUrl.trim()) return;
    const newEntry = {
      url: ingestUrl,
      coordinate: computed.coordinate,
      bytes: computed.bytesCount,
      territory: targetTerritoryStr,
      pulse: `${pulseDuration.toFixed(2)}s`,
      timestamp: 'Just now'
    };
    setCasteelianHistory([newEntry, ...casteelianHistory]);
    setIngestUrl('');
  };

  return (
    <div className="glass-panel p-8 border-lattice-cyan/20 bg-black/40 relative overflow-hidden group">
      {/* Network Pulse Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.05, 0.1, 0.05]
          }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(0,255,213,0.05)_0%,transparent_70%)]"
        />
      </div>

      <div className="relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center border transition-all duration-500 shadow-[0_0_30px_rgba(0,255,213,0.1)] ${ignited ? 'bg-lattice-cyan/20 border-lattice-cyan' : 'bg-white/5 border-white/10'}`}>
              <Wifi className={`w-7 h-7 ${ignited ? 'text-lattice-cyan animate-pulse' : 'text-white/20'}`} />
            </div>
            <div>
              <h2 className="text-3xl font-display font-bold text-white tracking-tighter uppercase whitespace-nowrap">
                Network <span className="text-lattice-cyan italic">Resonance</span>
              </h2>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${ignited ? 'bg-lattice-cyan shadow-[0_0_8px_rgba(0,255,213,1)]' : 'bg-white/20'}`} />
                <p className="text-[10px] font-mono text-white/40 uppercase tracking-[0.3em]">
                  {status?.ssid || 'SCANNING...'} :: {ignited ? 'ANCHORED' : 'PENDING IGNITION'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={fetchStatus}
              disabled={isSyncing}
              className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group/btn"
              title="Sync Status"
            >
              <RefreshCw className={`w-4 h-4 text-white/60 group-hover/btn:text-lattice-cyan ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={igniteHandshake}
              disabled={ignited || isSyncing}
              className={`px-6 py-3 rounded-xl font-mono font-bold text-[10px] uppercase tracking-[0.2em] transition-all flex items-center gap-3 ${ignited ? 'bg-lattice-cyan/10 text-lattice-cyan border border-lattice-cyan/50' : 'bg-lattice-cyan text-black hover:scale-105 shadow-[0_0_20px_rgba(0,255,213,0.3)]'}`}
            >
              {ignited ? <ShieldAlert className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
              {ignited ? 'IDENTITY SECURED' : 'IGNITE QR HANDSHAKE'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatusCard 
            icon={<Lock className="w-4 h-4" />} 
            label="Protocol" 
            value={status?.protocol || "WPA3_SOVEREIGN"} 
            subValue={status?.security || "TRUTH_BARRIER_IMPENDING"} 
          />
          <StatusCard 
            icon={<Zap className="w-4 h-4" />} 
            label="Resonance" 
            value={`${status?.resonance || 8.27} Hz`} 
            subValue="Zero-Drift Constant" 
            active={true}
          />
          <StatusCard 
            icon={<Globe className="w-4 h-4" />} 
            label="Nodes Active" 
            value={status?.nodes_active.toLocaleString() || "11,000,104,392"} 
            subValue="Target: 11B Collective" 
          />
          <StatusCard 
            icon={<Radio className="w-4 h-4" />} 
            label="Sync State" 
            value={status?.sync_status || "LOCKED"} 
            subValue={`Anchor: ${status?.anchor || "14350_BP"}`} 
            active={true}
          />
        </div>

        {/* Tab Selection */}
        <div className="mt-8 flex border-b border-white/10 pb-1 gap-2 overflow-x-auto">
          <button 
            onClick={() => setActiveSubTab('casteelian')} 
            className={`px-4 py-2 text-[10px] font-mono font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-2 ${activeSubTab === 'casteelian' ? 'text-lattice-cyan border-lattice-cyan bg-lattice-cyan/5' : 'text-white/40 border-transparent hover:text-white/80'}`}
          >
            <Network className="w-3.5 h-3.5" />
            🛰️ Casteelian Tunnel & Mesh Routing
          </button>
          <button 
            onClick={() => setActiveSubTab('qr')} 
            className={`px-4 py-2 text-[10px] font-mono font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-2 ${activeSubTab === 'qr' ? 'text-lattice-cyan border-lattice-cyan bg-lattice-cyan/5' : 'text-white/40 border-transparent hover:text-white/80'}`}
          >
            <QrCode className="w-3.5 h-3.5" />
            📱 Hotspot QR Access
          </button>
        </div>

        <div className="mt-6">
          <AnimatePresence mode="wait">
            {activeSubTab === 'qr' && (
              <motion.div
                key="qr"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col md:flex-row items-center gap-8"
              >
                <div className="flex-1 space-y-4 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-lattice-cyan/10 border border-lattice-cyan/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-lattice-cyan animate-pulse" />
                    <span className="text-[8px] font-mono text-lattice-cyan font-bold uppercase tracking-widest text-nowrap">WPA3 Sovereign Handshake Active</span>
                  </div>
                  <h3 className="text-xl font-display font-medium text-white">Universal Mesh Visibility</h3>
                  <p className="text-xs font-mono text-white/40 leading-relaxed max-w-xl uppercase tracking-tighter">
                    The Lattice mesh forces visibility for all joining devices. By utilizing this QR ignition protocol, clients execute a seamless hardware-level handshake directly with the Desk AP Core (Node 2) and bypass typical 1.0 de-auth beacons.
                  </p>
                  <div className="p-4 rounded-xl bg-black/60 border border-white/5 space-y-2 max-w-xl">
                    <div className="flex justify-between items-center text-xs font-mono text-white/60">
                      <span>MESH SSID:</span>
                      <span className="text-white font-bold select-all">AZL_LATTICE_MESH</span>
                    </div>
                    <div className="flex justify-between items-center text-xs font-mono text-white/60">
                      <span>WPA PASSPHRASE:</span>
                      <span className="text-lattice-cyan font-bold select-all">zero_drift_logic</span>
                    </div>
                  </div>
                </div>
                
                <div className="w-full md:w-64 space-y-4">
                  <div className="relative group/qr-container">
                    <div className="aspect-square bg-white rounded-2xl p-2 flex items-center justify-center shadow-2xl overflow-hidden relative">
                       <QRCodeSVG 
                        value={`WIFI:S:AZL_LATTICE_MESH;T:WPA;P:zero_drift_logic;;`} 
                        size={256}
                        level="L"
                        includeMargin={true}
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        className="w-full h-full"
                       />
                    </div>
                    <div className="mt-4 p-3 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20 text-center">
                      <p className="text-[8px] font-mono text-lattice-cyan uppercase tracking-widest leading-tight">
                        WPA3 Sovereign handshake ready
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeSubTab === 'casteelian' && (
              <motion.div
                key="casteelian"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-8"
              >
                {/* Visual Pipeline Header */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column: Interactive Parser Console */}
                  <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-5">
                    <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                      <Terminal className="w-5 h-5" />
                      <h4 className="text-sm font-display font-bold uppercase tracking-wider">Casteelian Proxy Ingestion Console</h4>
                    </div>
                    
                    <p className="text-[10px] font-mono text-white/40 leading-relaxed uppercase">
                      Enter any URL, IP, or raw packet tag below. The system converts the bytes into an infinite fractional coordinate anchored to the Miyake 14350 BP Horizon, determining which territory the packet is routed to.
                    </p>

                    <div className="space-y-1.5">
                      <label className="text-[8px] font-mono text-white/40 uppercase block">Intercepted Target (URL or Packet Hash)</label>
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          value={ingestUrl}
                          onChange={(e) => setIngestUrl(e.target.value)}
                          placeholder="e.g. paidingattention.org"
                          className="flex-1 bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-white focus:outline-none focus:border-lattice-cyan/50"
                        />
                        {ingestUrl.trim() && (
                          <button 
                            onClick={handleCommitToLedger}
                            className="px-4 bg-lattice-cyan text-black font-mono font-bold text-xs rounded-xl hover:scale-105 transition-all uppercase whitespace-nowrap"
                          >
                            Lock to Ledger
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Numerical Computation readout */}
                    <div className="space-y-3 p-4 rounded-xl bg-black/40 border border-white/5">
                      <div className="flex justify-between text-[10px] font-mono">
                        <span className="text-white/40 uppercase">Byte length:</span>
                        <span className="text-white font-bold">{computed.bytesCount} bytes</span>
                      </div>
                      
                      <div className="flex justify-between text-[10px] font-mono">
                        <span className="text-white/40 uppercase text-xs">Sum of Bytes (Base-256):</span>
                        <span className="text-white/80 select-all font-bold overflow-hidden text-ellipsis whitespace-nowrap max-w-[200px]" title="Summation of Byte Array base-256 fractions">
                          ∑(B_i / 256^i)
                        </span>
                      </div>

                      <div className="space-y-1 pt-1.5 border-t border-white/5">
                        <span className="text-[8px] font-mono text-lattice-cyan/60 uppercase block">Casteelian Fractional Coordinate (14350 BP Scaled):</span>
                        <div className="p-2.5 bg-black/80 rounded border border-lattice-cyan/20 select-all font-mono text-[11px] text-lattice-cyan tracking-tight break-all font-bold">
                          {computed.coordinate}
                        </div>
                      </div>

                      <div className="flex justify-between items-center text-[10px] font-mono pt-1">
                        <span className="text-white/40 uppercase">Active Destination:</span>
                        <span className={`font-bold px-2 py-0.5 rounded text-[9px] uppercase ${isLowerTerritory ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-lattice-cyan/10 text-lattice-cyan border border-lattice-cyan/20'}`}>
                          {isLowerTerritory ? 'Territory 1 (Desk Core)' : 'Territory 2 (Room Relay)'}
                        </span>
                      </div>
                    </div>

                    {/* Simulated Physical LED Pulse */}
                    <div className="p-4 rounded-xl bg-black/50 border border-white/5 flex items-center justify-between">
                      <div className="space-y-1">
                        <span className="text-[8px] font-mono text-white/30 uppercase block font-bold">ESP32 Onboard LED Pulse Indicator (Pin 2)</span>
                        <span className="text-[10px] font-mono text-white/60">
                          Pulse Interval: <span className="text-white font-bold">{(pulseDuration).toFixed(3)}s</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-[8px] font-mono text-white/20 uppercase">PHYSICAL BURST:</div>
                        <div className="relative w-7 h-7 flex items-center justify-center">
                          {/* Inner pulsing core */}
                          <div 
                            className="w-4 h-4 rounded-full bg-red-500 shadow-[0_0_15px_#ef4444]"
                            style={{
                              animation: `pulseAnimation ${pulseDuration}s infinite ease-in-out`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Schema Map explanation */}
                  <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-6">
                    <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                      <Sliders className="w-5 h-5" />
                      <h4 className="text-sm font-display font-bold uppercase tracking-wider">Cyber-Physical Loop Configuration</h4>
                    </div>

                    <p className="text-[11px] font-mono text-white/40 leading-relaxed uppercase">
                      The wireless network no longer operates in a vacuum. It is now hardwired to the operating system's raw networking stack:
                    </p>

                    {/* Step-by-Step Flow Tracker */}
                    <div className="space-y-4">
                      <FlowStep 
                        step="1" 
                        title="OS Interface Intercept" 
                        desc="Linux TUN interface (azl0 at 10.0.0.1) catches raw kernel packets." 
                      />
                      <FlowStep 
                        step="2" 
                        title="Proxy Gateway Ingestion" 
                        desc="Flask API Proxy Server on port 8080 resolves the target string with 100-digit precision." 
                      />
                      <FlowStep 
                        step="3" 
                        title="Wireless Air-Broadcast" 
                        desc="Cuts the physical serial cord and broadcasts the unrounded decimal coordinate over the airwaves via UDP socket on port 5006 targeting Node 2 on 192.168.4.1." 
                      />
                      <FlowStep 
                        step="4" 
                        title="Direct Node 2 Ingest" 
                        desc="Node 2 receives the wireless UDP stream directly on port 5006, updating live matrix variables and LED status pulses on the fly." 
                      />
                      <FlowStep 
                        step="5" 
                        title="Territorial Partitioning" 
                        desc="If coordinate < 0.5, Node 2 blinks (Territory 1). If >= 0.5, Node 1 blinks (Territory 2)." 
                      />
                    </div>
                  </div>
                </div>
                              {/* Ledger Registry Table */}
                <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-white/5">
                    <div className="flex items-center gap-2 text-lattice-cyan">
                      <Database className="w-5 h-5" />
                      <h4 className="text-sm font-display font-bold uppercase">Casteelian Ledger Registry</h4>
                    </div>
                               <div className="flex gap-2 bg-black/40 p-1 rounded-xl border border-white/10">
                      <button
                        onClick={() => setLedgerTab('local')}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-wider transition-all ${
                          ledgerTab === 'local'
                            ? 'bg-lattice-cyan/10 text-lattice-cyan font-bold border border-lattice-cyan/20'
                            : 'text-white/40 hover:text-white/80'
                        }`}
                      >
                        Local Simulator
                      </button>
                      <button
                        onClick={() => {
                          setLedgerTab('cloud');
                          fetchCloudLedger();
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                          ledgerTab === 'cloud'
                            ? 'bg-emerald-500/10 text-emerald-400 font-bold border border-emerald-500/20'
                            : 'text-white/40 hover:text-white/80'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Cloud Sync ({cloudEntries.length})
                      </button>
                      <button
                        onClick={() => {
                          setLedgerTab('replit');
                          fetchReplitLedger();
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                          ledgerTab === 'replit'
                            ? 'bg-red-500/10 text-red-400 font-bold border border-red-500/20'
                            : 'text-white/40 hover:text-white/80'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                        Replit Sync ({replitEntries.length})
                      </button>
                    </div>
                  </div>
 
                  <div className="overflow-x-auto">
                    {ledgerTab === 'local' ? (
                      <table className="w-full text-left text-xs font-mono">
                        <thead>
                          <tr className="border-b border-white/5 text-[9px] text-white/40 uppercase">
                            <th className="py-2.5">Ingested Identifier</th>
                            <th className="py-2.5">Bytes</th>
                            <th className="py-2.5">Casteelian Coordinate</th>
                            <th className="py-2.5">Claimed Domain</th>
                            <th className="py-2.5">Blink Pulse</th>
                            <th className="py-2.5 text-right">Age</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.02]">
                          {casteelianHistory.map((entry, index) => (
                            <tr key={index} className="hover:bg-white/[0.01] transition-colors">
                              <td className="py-3 text-white font-medium select-all">{entry.url}</td>
                              <td className="py-3 text-white/60">{entry.bytes}</td>
                              <td className="py-3 text-lattice-cyan select-all text-[10px] max-w-[200px] truncate" title={entry.coordinate}>{entry.coordinate}</td>
                              <td className="py-3">
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${entry.territory.includes('Territory 1') ? 'bg-emerald-500/10 text-emerald-400' : 'bg-lattice-cyan/10 text-lattice-cyan'}`}>
                                  {entry.territory}
                                </span>
                              </td>
                              <td className="py-3 text-white/40">{entry.pulse}</td>
                              <td className="py-3 text-white/30 text-right text-[10px]">{entry.timestamp}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : ledgerTab === 'cloud' ? (
                      <div className="space-y-4">
                        {cloudEntries.length === 0 ? (
                          <div className="text-center py-8 text-white/30 font-mono text-xs uppercase space-y-2">
                            <p>No high-precision cloud ledger entries synchronized yet.</p>
                            <p className="text-[10px] text-white/20">Stream coordinate updates from your laptop loop to display them here in real-time!</p>
                          </div>
                        ) : (
                          <table className="w-full text-left text-xs font-mono">
                            <thead>
                              <tr className="border-b border-white/5 text-[9px] text-white/40 uppercase">
                                <th className="py-2.5">Synced Domain/Packet</th>
                                <th className="py-2.5">Unrounded Coordinate</th>
                                <th className="py-2.5">Calculated Territory</th>
                                <th className="py-2.5 text-right">Sync Time</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.02]">
                              {cloudEntries.map((entry, index) => {
                                const floatVal = parseFloat(entry.coordinate);
                                const isLower = floatVal < 0.5;
                                const dateStr = new Date(entry.timestamp * 1000).toLocaleTimeString();
                                return (
                                  <tr key={index} className="hover:bg-white/[0.01] transition-colors">
                                    <td className="py-3 text-white font-medium select-all">{entry.url}</td>
                                    <td className="py-3 text-red-400 select-all text-[10px] max-w-[200px] truncate" title={entry.coordinate}>{entry.coordinate}</td>
                                    <td className="py-3">
                                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${isLower ? 'bg-emerald-500/10 text-emerald-400' : 'bg-lattice-cyan/10 text-lattice-cyan'}`}>
                                        {isLower ? 'Territory 1 (Desk Core)' : 'Territory 2 (Room Relay)'}
                                      </span>
                                    </td>
                                    <td className="py-3 text-white/30 text-right text-[10px]">{dateStr}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {replitEntries.length === 0 ? (
                          <div className="text-center py-8 text-white/30 font-mono text-xs uppercase space-y-2">
                            <p>No Replit engine UDP packets received yet.</p>
                            <p className="text-[10px] text-white/20">Awaiting superluminal coordinate streams on port 5006!</p>
                          </div>
                        ) : (
                          <table className="w-full text-left text-xs font-mono">
                            <thead>
                              <tr className="border-b border-white/5 text-[9px] text-white/40 uppercase">
                                <th className="py-2.5">Packet Index</th>
                                <th className="py-2.5">Real-time Streamed Coordinate</th>
                                <th className="py-2.5">Routing Array Target</th>
                                <th className="py-2.5 text-right">Arrival Time</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.02]">
                              {replitEntries.map((entry, index) => {
                                const floatVal = parseFloat(entry.coordinate);
                                const isLower = floatVal < 0.5;
                                const dateStr = new Date(entry.timestamp * 1000).toLocaleTimeString();
                                return (
                                  <tr key={index} className="hover:bg-white/[0.01] transition-colors">
                                    <td className="py-3 text-white/40">#{replitEntries.length - index}</td>
                                    <td className="py-3 text-lattice-cyan select-all text-[10px] max-w-[200px] truncate" title={entry.coordinate}>{entry.coordinate}</td>
                                    <td className="py-3">
                                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${isLower ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                        {isLower ? 'Desk Segment' : 'Room Relay'}
                                      </span>
                                    </td>
                                    <td className="py-3 text-white/30 text-right text-[10px]">{dateStr}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Embedded CSS for pulsing effect */}
      <style>{`
        @keyframes pulseAnimation {
          0%, 100% {
            transform: scale(0.85);
            opacity: 0.3;
            box-shadow: 0 0 5px rgba(239, 68, 68, 0.2);
          }
          50% {
            transform: scale(1.15);
            opacity: 1;
            box-shadow: 0 0 25px rgba(239, 68, 68, 1);
          }
        }
      `}</style>
    </div>
  );
};

interface StatusCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue: string;
  active?: boolean;
}

const StatusCard: React.FC<StatusCardProps> = ({ icon, label, value, subValue, active }) => (
  <div className={`p-5 rounded-2xl border transition-all duration-300 flex flex-col gap-3 group/card ${active ? 'bg-lattice-cyan/5 border-lattice-cyan/20 shadow-[0_0_15px_rgba(0,255,213,0.05)]' : 'bg-white/5 border-white/5'}`}>
    <div className={`flex items-center gap-2 transition-colors ${active ? 'text-lattice-cyan' : 'text-white/20'}`}>
      {icon}
      <span className="text-[9px] font-mono font-bold uppercase tracking-[0.2em]">{label}</span>
    </div>
    <div className="space-y-1">
      <div className={`text-xl font-display font-bold tracking-tight transition-colors ${active ? 'text-white' : 'text-white/60'}`}>{value}</div>
      <div className="text-[9px] font-mono text-white/20 uppercase tracking-tighter whitespace-nowrap overflow-hidden text-ellipsis">{subValue}</div>
    </div>
  </div>
);

interface FlowStepProps {
  step: string;
  title: string;
  desc: string;
}

const FlowStep: React.FC<FlowStepProps> = ({ step, title, desc }) => (
  <div className="flex gap-4 items-start text-xs font-mono">
    <div className="w-5 h-5 rounded-full bg-lattice-cyan/15 border border-lattice-cyan/35 flex items-center justify-center text-[10px] text-lattice-cyan font-bold flex-shrink-0 mt-0.5">
      {step}
    </div>
    <div className="space-y-0.5">
      <h5 className="text-white font-bold uppercase text-[10px] tracking-wider">{title}</h5>
      <p className="text-[10px] text-white/40 leading-relaxed uppercase">{desc}</p>
    </div>
  </div>
);
